import { MRT_ColumnDef, createMRTColumnHelper } from "material-react-table";
import { useTranslation } from "react-i18next";
import { GridText } from "src/components/Grid/GridText";
import { PackagingList } from "src/api/client-api/packagings/useGetAllPackagings";
import { extendByColumns, extendColumnsByScores } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { GridLink } from "src/components/Grid/GridLink";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { StringFilter } from "src/components/Grid/filters/StringFilter";
import { useTagsColumn } from "../../../components/Grid/hooks/tags/useTagsColumn";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { packagingListLayoutTokens } from "./packagingListLayoutTokens";
import { PackagingTypeTag } from "src/components/Packaging/PackagingTypeTag";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";

export type PackagingGridColumns = MRT_ColumnDef<PackagingList, any>[];

export const usePackagingColumns = (data: PackagingList[], gridKey: string): PackagingGridColumns => {
    const { t } = useTranslation();
    const isProductUxV2 = useIsProductUxV2();
    const appearance = isProductUxV2 ? "product-v2" : "legacy";

    const { getScoresByType } = useFoodpilotContext();
    const { company } = useCompanyMetadata();

    const packagingsScores = getScoresByType("packaging");

    const columnHelper = createMRTColumnHelper<PackagingList>();
    const { tagColumns } = useTagsColumn<PackagingList>({
        appearance: isProductUxV2 ? "product-v2" : "legacy",
        context: "packaging",
        gridKey,
    });
    const mrt_columns: PackagingGridColumns = [];

    const noValue = "-";
    mrt_columns.push(
        columnHelper.accessor((row) => row.name ?? noValue, {
            grow: false,
            id: "name",
            header: t("nom"),
            Cell: (value) => {
                if (value.row.original.canView) {
                    const packagingId = value.row.original.id;
                    const url = URLHandler.packaging.scores(company.id, packagingId);
                    return <GridLink appearance={appearance} to={url} label={value.renderedCellValue} />;
                }

                return <GridText appearance={appearance} value={value.renderedCellValue} />;
            },
            Filter: (mrtProps) => {
                return <StringFilter mrtProps={mrtProps} />;
            },
            enableSorting: true,
            enableColumnFilter: true,
        }),
    );

    mrt_columns.push(
        columnHelper.accessor((row) => row.externalId ?? noValue, {
            id: "externalId",
            header: t("unique_identifier"),
            Cell: (value) => <GridText appearance={appearance} value={value.renderedCellValue} />,

            Filter: (mrtProps) => {
                return <StringFilter mrtProps={mrtProps} />;
            },
            enableSorting: true,
            enableColumnFilter: true,
        }),
    );

    mrt_columns.push(
        columnHelper.accessor((row) => row["@type"] ?? "", {
            id: "type",
            header: t("packagings.column.type", "Type"),
            Cell: ({ row }) => {
                const type = row.original["@type"];
                if (type === "PackagingComposite") {
                    return (
                        <PackagingTypeTag
                            appearance={isProductUxV2 ? "hub" : "legacy"}
                            kind="composite"
                            label={
                                isProductUxV2 ?
                                    t("packaging.type.packaging", "Packaging")
                                :   t("packaging.type.unit", "Unit")
                            }
                        />
                    );
                }
                if (type === "PackagingElement") {
                    return (
                        <PackagingTypeTag
                            appearance={isProductUxV2 ? "hub" : "legacy"}
                            kind="element"
                            label={
                                isProductUxV2 ?
                                    t("packaging.type.element", "Element")
                                :   t("packaging.type.component", "Component")
                            }
                        />
                    );
                }

                return <GridText appearance={appearance} value={noValue} />;
            },
            enableSorting: false,
            enableColumnFilter: false,
        }),
    );

    extendColumnsByScores(mrt_columns, data ?? [], packagingsScores, { appearance });
    extendByColumns(mrt_columns, tagColumns);

    mrt_columns.sort((leftColumn, rightColumn) => {
        const leftId = leftColumn.accessorKey ?? leftColumn.id ?? "";
        const rightId = rightColumn.accessorKey ?? rightColumn.id ?? "";
        const leadingColumnOrder = packagingListLayoutTokens.leadingColumnOrder as readonly string[];
        const leftIndex = leadingColumnOrder.indexOf(String(leftId));
        const rightIndex = leadingColumnOrder.indexOf(String(rightId));

        if (leftIndex === -1 && rightIndex === -1) return 0;
        if (leftIndex === -1) return 1;
        if (rightIndex === -1) return -1;
        return leftIndex - rightIndex;
    });

    return mrt_columns;
};
